import { useState, useEffect, useCallback } from "react";
import { GitBranch, Loader2, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useMe } from "../../hooks/useUser";
import { mutate } from "../../graphql/client";
import { UPDATE_BRANCH_SETTINGS, REORDER_BRANCHES } from "../../graphql/operations";
import { useUIStore } from "../../stores/uiStore";
import type { UserBranch, UpdateBranchSettingsInput, ReorderBranchesInput } from "../../types/api";

interface BranchesModalProps {
  open: boolean;
  onClose: () => void;
}

interface SortableBranchItemProps {
  branch: UserBranch;
  onToggleSortDirection: (branch: string) => void;
  isUpdating: boolean;
}

function SortableBranchItem({ branch, onToggleSortDirection, isUpdating }: SortableBranchItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: branch.branch });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-2 py-3 rounded-lg hover:bg-[hsl(var(--muted))] transition-colors bg-[hsl(var(--background))]"
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 cursor-grab active:cursor-grabbing text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <div className="h-8 w-8 rounded-md bg-[hsl(var(--muted))] flex items-center justify-center flex-shrink-0">
        <GitBranch className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-mono text-sm truncate">{branch.branch}</div>
      </div>

      <div className="text-sm text-[hsl(var(--muted-foreground))] tabular-nums flex-shrink-0">
        {branch.commitCount}
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 flex-shrink-0"
        onClick={() => onToggleSortDirection(branch.branch)}
        disabled={isUpdating}
        title={branch.commitSortDirection === 'desc' ? 'Showing newest first (click for oldest first)' : 'Showing oldest first (click for newest first)'}
      >
        {branch.commitSortDirection === 'desc' ? (
          <ArrowDown className="h-4 w-4" />
        ) : (
          <ArrowUp className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
}

export default function BranchesModal({ open, onClose }: BranchesModalProps) {
  const { user, loading, refetch } = useMe();
  const [localBranches, setLocalBranches] = useState<UserBranch[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const notifyProfileSubscribers = useUIStore((s) => s.notifyProfileSubscribers);

  // Refetch user data when modal opens to get latest branches
  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);

  // Sync local state with user data
  useEffect(() => {
    if (user?.branches) {
      // Sort by sortOrder to maintain order
      const sorted = [...user.branches].sort((a, b) => a.sortOrder - b.sortOrder);
      setLocalBranches(sorted);
    }
  }, [user?.branches]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalBranches((items) => {
        const oldIndex = items.findIndex((item) => item.branch === active.id);
        const newIndex = items.findIndex((item) => item.branch === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update sortOrder locally
        return newItems.map((item, index) => ({
          ...item,
          sortOrder: index,
        }));
      });

      // Save to server
      setIsUpdating(true);
      try {
        // Calculate the new order
        const oldIndex = localBranches.findIndex((item) => item.branch === active.id);
        const newIdx = localBranches.findIndex((item) => item.branch === over.id);
        const reordered = arrayMove(localBranches, oldIndex, newIdx);

        const input: ReorderBranchesInput = {
          branches: reordered.map((b, i) => ({
            branch: b.branch,
            sortOrder: i,
          })),
        };

        await mutate(REORDER_BRANCHES, { input });
        await refetch();
        // Notify profile subscribers to refetch user data
        notifyProfileSubscribers();
      } catch (error) {
        console.error('Failed to reorder branches:', error);
        // Revert to server state
        if (user?.branches) {
          const sorted = [...user.branches].sort((a, b) => a.sortOrder - b.sortOrder);
          setLocalBranches(sorted);
        }
      } finally {
        setIsUpdating(false);
      }
    }
  }, [localBranches, refetch, user?.branches, notifyProfileSubscribers]);

  const handleToggleSortDirection = useCallback(async (branchName: string) => {
    const branch = localBranches.find((b) => b.branch === branchName);
    if (!branch) return;

    const newDirection = branch.commitSortDirection === 'desc' ? 'asc' : 'desc';

    // Optimistic update
    setLocalBranches((items) =>
      items.map((item) =>
        item.branch === branchName
          ? { ...item, commitSortDirection: newDirection }
          : item
      )
    );

    setIsUpdating(true);
    try {
      const input: UpdateBranchSettingsInput = {
        branch: branchName,
        commitSortDirection: newDirection,
      };

      await mutate(UPDATE_BRANCH_SETTINGS, { input });
      await refetch();
      // Notify profile subscribers to refetch user data
      notifyProfileSubscribers();
    } catch (error) {
      console.error('Failed to update branch settings:', error);
      // Revert
      setLocalBranches((items) =>
        items.map((item) =>
          item.branch === branchName
            ? { ...item, commitSortDirection: branch.commitSortDirection }
            : item
        )
      );
    } finally {
      setIsUpdating(false);
    }
  }, [localBranches, refetch, notifyProfileSubscribers]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[440px] max-h-[85vh] flex flex-col">
        <DialogHeader className="pb-4 border-b border-[hsl(var(--border))] flex-shrink-0">
          <DialogTitle className="font-mono flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Branches
          </DialogTitle>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Drag to reorder. Click arrow to change commit sort direction.
          </p>
        </DialogHeader>

        <div className="py-2 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--muted-foreground))]" />
            </div>
          ) : localBranches.length === 0 ? (
            <div className="text-center py-8">
              <GitBranch className="h-12 w-12 mx-auto mb-4 text-[hsl(var(--muted-foreground))]" />
              <p className="text-[hsl(var(--muted-foreground))]">
                No branches yet
              </p>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                Create a commit to start a new branch
              </p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localBranches.map((b) => b.branch)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-1">
                  {localBranches.map((branch) => (
                    <SortableBranchItem
                      key={branch.branch}
                      branch={branch}
                      onToggleSortDirection={handleToggleSortDirection}
                      isUpdating={isUpdating}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
